import { Fragment, useRef } from 'react';
// @mui
import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
//
import { STAGE, TYPE } from '../../../theme/stage';
import { srOnly } from '../a11y';
import type { Line } from '../copy';
import { CSS_EASE } from '../motion';
import Accent from './Accent';
import { entrance, riseIn } from './entrance';
import { useRevealed } from './Reveal';
import Rich from './Rich';

// ----------------------------------------------------------------------

type LineMode = 'block' | 'inline';

type Props = {
  as: 'h1' | 'h2';
  id: string;
  lines: [Line, Line];
  size: 'hero' | 'billboard';
  lineMode: LineMode | { xs: LineMode; sm: LineMode };
  align?: 'left' | 'center';
  srPrefix?: string;
  enter: 'load' | 'view';
  sx?: SxProps<Theme>;
};

const SIZE = { hero: TYPE.heroTitle, billboard: TYPE.billboard };

const MASK = {
  block: { display: 'block', verticalAlign: 'baseline', whiteSpace: 'normal' },
  inline: { display: 'inline-block', verticalAlign: 'top', whiteSpace: 'nowrap' },
} as const;

const LOAD_DELAY = [500, 560];

const VIEW_DELAY = [0, 90];

function LineText({ line, index }: { line: Line; index: number }) {
  if (typeof line === 'string') {
    return <>{line}</>;
  }
  const [only] = line;
  if (index === 1 && line.length === 1 && typeof only !== 'string' && 'i' in only) {
    return <Accent tone="ink2">{only.i}</Accent>;
  }
  return <Rich parts={line} />;
}

function maskSx(lineMode: Props['lineMode']) {
  if (typeof lineMode === 'string') {
    return MASK[lineMode];
  }
  const xs = MASK[lineMode.xs];
  const sm = MASK[lineMode.sm];
  return {
    display: { xs: xs.display, sm: sm.display },
    verticalAlign: { xs: xs.verticalAlign, sm: sm.verticalAlign },
    whiteSpace: { xs: xs.whiteSpace, sm: sm.whiteSpace },
  };
}

export default function TwoTone({
  as,
  id,
  lines,
  size,
  lineMode,
  align,
  srPrefix,
  enter,
  sx,
}: Props) {
  const ref = useRef<HTMLHeadingElement>(null);

  const revealed = useRevealed(ref);

  return (
    <Box
      ref={ref}
      component={as}
      id={id}
      className={enter === 'view' && revealed ? 'is-in' : undefined}
      sx={[
        {
          ...SIZE[size],
          m: 0,
          color: STAGE.ink,
          textAlign: align,
          '&.is-in [data-enter]': { transform: 'none' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {srPrefix && (
        <Box component="span" sx={srOnly}>
          {srPrefix}
        </Box>
      )}
      {lines.map((line, index) => (
        <Fragment key={index}>
          {index === 1 && ' '}
          <Box
            component="span"
            sx={{
              ...maskSx(lineMode),
              overflow: 'hidden',
              pb: '.12em',
              mb: '-.12em',
              textWrap: { xs: 'balance', sm: 'wrap' },
              ...(index === 1 && { color: STAGE.mute, fontWeight: 400 }),
            }}
          >
            <Box
              component="span"
              data-enter
              sx={{
                display: 'inline-block',
                verticalAlign: 'top',
                ...(enter === 'load'
                  ? entrance(riseIn, { duration: 900, delay: LOAD_DELAY[index] })
                  : {
                      transform: 'translateY(105%)',
                      transition: `transform 900ms ${CSS_EASE.outExpo} ${VIEW_DELAY[index]}ms`,
                    }),
              }}
            >
              <LineText line={line} index={index} />
            </Box>
          </Box>
        </Fragment>
      ))}
    </Box>
  );
}
